import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectProducts } from '../store/products';
import { selectSelectedCurrency } from '../store/user';
import useCart from './cart/useCart';
import useHandleAddProduct from '../utils/handleAddProduct';
import { openSheet } from '../store/sheetToggler';

/**
 * Custom hook to set up global Builder.io handler
 * This hook exposes a global function that can be called from Builder.io CMS
 * Includes comprehensive error handling to prevent UI breaking
 */
export const useAddProductBuilderHandler = () => {
  const dispatch = useDispatch();
  const { handleAddToGiving: handleAddToGivingCart } = useCart();
  const [addProduct] = useHandleAddProduct();
  const allProducts = useSelector((state) => selectProducts(state));
  const selectedCurrency = useSelector(selectSelectedCurrency);

  // Internal function to handle product addition with error handling
  const handleAddProduct = (selectedProduct, quantity = 1, recurring = 'one-off', currency = 'AUD', addToCart = false) => {
    try {
      // Validate inputs
      if (!selectedProduct || !selectedProduct.creator) {
        console.error('Invalid product data:', selectedProduct);
        return;
      }

      if (!allProducts || !Array.isArray(allProducts)) {
        console.error('Products not loaded yet');
        return;
      }

      console.log('Processing product:', { selectedProduct, quantity, recurring, currency, addToCart });

      // Find the product in allProducts with error handling
      let foundProduct = selectedProduct;
      
      try {
        allProducts.forEach((item) => {
          if (!item || !item.sub_categories) return;
          
          item.sub_categories.forEach((inner_item) => {
            if (!inner_item || !inner_item.products) return;
            
            inner_item.products.forEach((inner_item_product) => {
              try {
                if (inner_item_product && 
                    Number(inner_item_product.status) === 1 &&
                    String(inner_item_product.creator) === String(selectedProduct.creator)) {
                  foundProduct = inner_item_product;
                }
              } catch (productError) {
                console.error('Error processing individual product:', productError);
              }
            });
          });
        });
      } catch (searchError) {
        console.error('Error searching products:', searchError);
        // Continue with original product if search fails
      }

      // Validate found product
      if (!foundProduct || !foundProduct.creator) {
        console.error('Product not found:', selectedProduct.creator);
        return;
      }

      // Create temp product with safe defaults
      const tempProduct = {
        ...foundProduct,
        quantity: Number(quantity) || 1,
        recurring: recurring || 'one-off',
      };

      // Add to cart with error handling
      try {
        if (addToCart) {
          if (typeof handleAddToGivingCart === 'function') {
            handleAddToGivingCart(tempProduct, 'custom');
          }
          if (typeof dispatch === 'function') {
            dispatch(openSheet());
          }
        } else {
          if (typeof addProduct === 'function') {
            addProduct({ product: tempProduct, currency: currency || 'AUD' });
          }
        }
        
        console.log('Product added successfully:', tempProduct);
      } catch (cartError) {
        console.error('Error adding to cart:', cartError);
      }

    } catch (error) {
      console.error('Error in handleAddProduct:', error);
    }
  };

  useEffect(() => {
    try {
      // Expose a global function for Builder.io with error handling
      window.builderClick = (selectedProduct, quantity = 1, recurring = 'one-off', addToCart = false) => {
        try {
          handleAddProduct(
            selectedProduct, 
            quantity, 
            recurring, 
            selectedCurrency || 'AUD', 
            addToCart
          );
          console.log('Builder.io handler triggered successfully');
        } catch (error) {
          console.error('Error in builderClick:', error);
        }
      };

      return () => {
        try {
          delete window.builderClick;
        } catch (cleanupError) {
          console.error('Error cleaning up builderClick:', cleanupError);
        }
      };
    } catch (setupError) {
      console.error('Error setting up builderClick:', setupError);
    }
  }, [allProducts, selectedCurrency, handleAddToGivingCart, dispatch, addProduct]);

  return { builderClick: window.builderClick };
};